import { Button } from "./button";
import { DropDown } from "./drop_down";
import wretch from "wretch";
import { calcPM25 } from "../logic/utils";
import { useEffect, useState } from "react";
import { Spinner } from "./spinner";
import { useNavigate } from "react-router-dom";

import { ReactComponent as AqiGood } from "../assets/images/calculator_aqi_good.svg";
import { ReactComponent as AqiModerate } from "../assets/images/calculator_aqi_moderate.svg";
import { ReactComponent as AqiUnhealthy } from "../assets/images/calculator_aqi_unhealthy.svg";
import { ReactComponent as AqiDangerous } from "../assets/images/calculator_aqi_dangerous.svg";
import { ReactComponent as AqiHazardous } from "../assets/images/calculator_aqi_hazardous.svg";
import { ReactComponent as AqiCritical } from "../assets/images/calculator_aqi_critical.svg";

import styles from "./styles/calculator_form.module.scss";
import { Divider } from "./divider";
import Strings from "../strings/strings";

enum LoadingState {
  Init,
  Loading,
  Result,
}

export const CalculatorForm = () => {
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.Init);
  const [selectedCity, setSelectedCity] = useState(Strings.seoul);
  const [calculation, setCalculation] = useState({
    AQI: "",
    hours: 15,
    cigs: 0,
    particles: 0,
  });

  /// REFACTOR -- use maps to get coordinates
  const cityCoordinates: Record<string, { lat: number; lon: number }> = {
    Seoul: { lat: 37.5665, lon: 126.978 },
    Hong_Kong: { lat: 22.3193, lon: 114.1694 },
    Kuala_Lumpur: { lat: 3.139, lon: 101.6869 },
    Singapour: { lat: 1.3521, lon: 103.8198 },
    Jakarta: { lat: -6.2088, lon: 106.8456 },
    Rio_de_Janeiro: { lat: -22.9068, lon: -43.1729 },
    Mexico_City: { lat: 19.4326, lon: -99.1332 },
    Tokyo: { lat: 35.6762, lon: 139.6503 },
    Bejing: { lat: 39.9042, lon: 116.4074 },
  };

  const calculateCigsFromAQI = (AQI: number, hours: number) => {
    const PM24h = calcPM25(AQI); // Estimate PM2.5 from AQI
    const cigs = (PM24h / 22 / 24) * hours; // Core logic
    return cigs;
  };

  const getAqiFromCoordinates = async () => {
    setLoadingState(LoadingState.Loading);
    const coords = cityCoordinates[selectedCity];
    if (!coords) return;

    const response = await wretch(
      `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${coords.lat}&longitude=${coords.lon}&current=us_aqi&past_days=1`
    )
      .get()
      .res((r) => {
        setTimeout(() => {
          setLoadingState(LoadingState.Result);
        }, 500);
        return r.json();
      });

    const currentAQI = response.current.us_aqi;
    const hoursExposed = calculation.hours;
    const cigs = calculateCigsFromAQI(currentAQI, hoursExposed);

    setCalculation({
      ...calculation,
      AQI: currentAQI,
      cigs: cigs,
    });
  };

  const render = (state: LoadingState) => {
    switch (state) {
      case LoadingState.Init:
        return <Table />;
      case LoadingState.Loading:
        return <Loader />;
      case LoadingState.Result:
        return <Result aqi={Number(calculation.AQI)} />;
    }
  };

  const Table: React.FC = () => {
    return (
      <>
        <div className={styles.wrapper}>
          <DropDown
            label={Strings.city}
            onChange={(value) => setSelectedCity(value)}
            value={selectedCity}
            options={[
              { value: "Seoul", label: Strings.seoul },
              { value: "Hong_Kong", label: Strings.hongKong },
              { value: "Kuala_Lumpur", label: Strings.kualaLumpur },
              { value: "Singapour", label: Strings.singapour },
              { value: "Jakarta", label: Strings.jakarta },
              { value: "Rio_de_Janeiro", label: Strings.rioDeJaneiro },
              { value: "Mexico_City", label: Strings.mexicoCity },
              { value: "Tokyo", label: Strings.tokyo },
              { value: "Bejing", label: Strings.beijing },
            ]}
          />
          <div className={styles.buttons}>
            {Button({
              text: Strings.airQualityBtn,
              width: "100%",
              onClick: () => getAqiFromCoordinates(),
            })}
          </div>
        </div>
      </>
    );
  };

  const Loader: React.FC = () => {
    return (
      <>
        <div className={styles.wrapper}>
          <div className={styles.spinner}>
            <Spinner />
          </div>
        </div>
      </>
    );
  };

  const Result: React.FC<{ aqi: number }> = ({ aqi }) => {
    const navigate = useNavigate();
    const [pollution, setPollution] = useState("");

    useEffect(() => {
      if (aqi <= 50) setPollution("good");
      else if (aqi <= 100) setPollution("moderate");
      else if (aqi <= 150) setPollution("unhealthy");
      else if (aqi <= 200) setPollution("dangerous");
      else if (aqi <= 300) setPollution("critical");
      else setPollution("hazardous");
    }, [aqi]);

    const renderComponentByAqi = () => {
      if (aqi <= 50) return <AqiGood />;
      else if (aqi <= 100) return <AqiModerate />;
      else if (aqi <= 150) return <AqiUnhealthy />;
      else if (aqi <= 200) return <AqiDangerous />;
      else if (aqi <= 300) return <AqiCritical />;
      else return <AqiHazardous />;
    };

    return (
      <>
        <div className={styles.wrapper}>
          <div className={styles.againButton} onClick={() => navigate(0)}>
            {Strings.againBtn}
          </div>
          <div className={styles.resultContent}>
            {renderComponentByAqi()}
            <span>
              {Strings.airQualityIn}
              <b>{selectedCity.replaceAll("_", " ")}</b>
              {Strings.is}
              {pollution}
            </span>
          </div>
          <Divider width={"100%"} />
          <div className={styles.text}>
            <span>
              {Strings.workoutActivity}
              {calculation.cigs.toFixed(2)}
              {Strings.cigarettesText}
              {(calculation.cigs * 7).toFixed(2)}
              {Strings.cigarettesWeek}
              {(calculation.cigs * 30).toFixed(2)}
              {Strings.cigarettesMonth}
              {(calculation.cigs * 365).toFixed(2)}
              {Strings.cigarettesYear}
            </span>
          </div>
          <div className={styles.buttons}>
            {Button({
              text: Strings.joinApparaProducts,
              width: "100%",
              onClick: () => navigate("/products"),
            })}
          </div>
        </div>
      </>
    );
  };

  return <>{render(loadingState)}</>;
};
